import React, { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ItemDetails } from 'components'
import { Box, styled } from '@mui/material'
import ApplicationAccessLevel from './ApplicationAccessLevel'
import { ApplicationOverView } from './ApplicationOverView'
import { ApplicationAdditionalInformation } from './AdditionalDetails'
import { ApplicationActions } from './ApplicationActions'
import { ProtectedApplicationAssignments } from './ProtectedApplicationAssignments/ProtectedApplicationAssignments'
import useSubcomponents from 'useSubcomponents'
import { LocalSensitiveFunctions } from './LocalSensitiveFunctions'
import useConfiguration from 'useConfiguration'
import { useAccessRequestPolicy } from 'hooks'
import { ResourceTypesNamespace, useRegistry } from 'core'
import DynamicFieldTypesSelector from './DynamicFieldTypesSelector'

const applicationPrimaryTabExplain = {
    overview:
        'Overview tab: application friendly name, technical name, description, logo, optional Azure app metadata, access policy, and instructions.',
    additionalInformation:
        'More information tab: owners, deputies for Azure apps, responsible party, and related people for this catalog application.',
    action:
        'Actions tab: optional workflows your administrator exposed for this application (for example self-service or support flows).',
}

const applicationSubTabExplainByValue = {
    localSensitiveFunctions:
        'Local sensitive functions tab: functions tied to the access level you selected below; browse the list and use pagination.',
}

const SUBTABS = [
    {
        value: 'localSensitiveFunctions',
        label: 'Common_LocalFunctionalAccess',
        display: true,
        requireAccess: {
            control: 'ITShop-ApplicationDetails-SensitiveFunctions',
        },
    },
]

const ApplicationDetails = ({
    application,
    accessLevel,
    accessLevels,
    setAccessLevelValue,
    targetPerson,
    isCheckingAccess,
    applicationId,
    item,
}) => {
    const { t } = useTranslation()
    const { hasAccessToControl } = useSubcomponents()
    const [isFormValidated, setIsFormValidated] = useState(false)
    const registry = useRegistry()
    const applicationsType = registry.get(
        ResourceTypesNamespace,
        'Applications',
    )
    const subTabsAccess = SUBTABS.filter(
        (x) => x.display && hasAccessToControl(x.requireAccess?.control),
    )
    const [subActiveTab, setSubActiveTab] = useState(
        subTabsAccess && subTabsAccess.length > 0 ? subTabsAccess[0].value : '',
    )
    const { getResourceType } = useConfiguration()

    const currentResourceType = getResourceType('Applications')
    const [activeTab, setActiveTab] = useState('overview')

    const requestPolicy = useAccessRequestPolicy(
        accessLevel?.resourceAssignment?.requestPolicyId,
    )

    const fieldTypeSelector = () => {
        return (
            <DynamicFieldTypesSelector
                accessLevel={accessLevel?.resourceAssignment}
                isFormValidatedFromAccessLevel={isFormValidated}
            />
        )
    }
    return (
        <Fragment>
            <ItemDetails.Tabs
                variant="standard"
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                style={{ marginBottom: '2rem' }}
            >
                <ItemDetails.Tabs.Tab
                    value="overview"
                    label={t('Common_Overview')}
                    data-ai-explain={applicationPrimaryTabExplain.overview}
                />
                <ItemDetails.Tabs.Tab
                    value="additionalInformation"
                    label={t('Common_MoreInformation')}
                    data-ai-explain={
                        applicationPrimaryTabExplain.additionalInformation
                    }
                />
                <ItemDetails.Tabs.Tab
                    value="action"
                    label={t('Common_Actions')}
                    data-ai-explain={applicationPrimaryTabExplain.action}
                />
            </ItemDetails.Tabs>
            {activeTab === 'overview' && (
                <ApplicationOverView
                    data={application}
                    policy={requestPolicy}
                />
            )}
            {activeTab === 'additionalInformation' && (
                <ApplicationAdditionalInformation
                    isAzureApplication={application.isAzureApplication}
                    applicationId={applicationId}
                />
            )}
            {activeTab === 'action' && (
                <ApplicationActions data={application} />
            )}

            {applicationId && (
                <ProtectedApplicationAssignments
                    application={application}
                    applicationsType={currentResourceType}
                    applicationId={applicationId}
                    applicationTypeRegistry={applicationsType}
                />
            )}

            {application && (
                <Box
                    minWidth={'100%'}
                    data-ai-explain="Access level: choose how much access you are requesting (management role, local right, Azure role, etc.), complete any required fields, then add the application to your cart."
                >
                    <ApplicationAccessLevel
                        accessLevel={accessLevel}
                        accessLevels={accessLevels?.filter(
                            (a) => !a.isAssigned,
                        )}
                        setAccessLevelValue={(val) => setAccessLevelValue(val)}
                        isCheckingAccess={isCheckingAccess}
                        application={application}
                        targetPerson={targetPerson}
                        fieldTypeSelector={fieldTypeSelector()}
                        setIsFormValidated={(val) => setIsFormValidated(val)}
                        isFormValidated={isFormValidated}
                    />
                </Box>
            )}
            {accessLevel && (
                <Fragment>
                    <Box paddingRight="12px">
                        <ItemDetails.Tabs
                            variant={'scrollable'}
                            value={subActiveTab}
                            onChange={(_, value) => setSubActiveTab(value)}
                        >
                            {subTabsAccess.map((subTab, i) => {
                                return (
                                    <ItemDetails.Tabs.Tab
                                        key={subTab.value || i}
                                        value={subTab.value}
                                        label={t(subTab.label)}
                                        data-protectedsubcomponent={
                                            subTab.requireAccess?.control
                                        }
                                        data-ai-explain={
                                            applicationSubTabExplainByValue[
                                                subTab.value
                                            ] ||
                                            `Details tab: ${t(subTab.label)}`
                                        }
                                    />
                                )
                            })}
                        </ItemDetails.Tabs>
                    </Box>

                    <Box overflow="auto">
                        {subActiveTab === 'localSensitiveFunctions' && (
                            <LocalSensitiveFunctions
                                resourceAssignment={
                                    accessLevel?.resourceAssignment
                                }
                            />
                        )}
                    </Box>
                </Fragment>
            )}
        </Fragment>
    )
}

export default ApplicationDetails
